import React, { useState, useEffect } from "react";
import { Button, Typography, Card, message } from "antd";
import { KeyOutlined, PlusOutlined } from "@ant-design/icons";
import {
  createPermission,
  deletePermission,
  getAllPermissions,
  updatePermission,
} from "../../api/permissions/permissionRequest";
import { predefinedColors } from "../../utils";
import PermissionTable from "./PermissionTable";
import PermissionModal from "./PermissionModal";
import PermissionRender from "../../components/PermissionRender";

const { Title } = Typography;

const PermissionsPage = () => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentPermission, setCurrentPermission] = useState(null);
  const [resources, setResources] = useState([]);
  const [actions, setActions] = useState([]);
  const [resourceColors, setResourceColors] = useState({});
  const [actionColors, setActionColors] = useState({});

  useEffect(() => {
    fetchPermissions();
  }, []);

  const fetchPermissions = async () => {
    setLoading(true);
    try {
      const data = await getAllPermissions();
      setPermissions(data);

      const uniqueResources = [...new Set(data.map((p) => p.resource))].sort();
      setResources(uniqueResources);

      const uniqueActions = [...new Set(data.map((p) => p.action))].sort();
      setActions(uniqueActions);

      const newResourceColors = {};
      uniqueResources.forEach((resource, index) => {
        newResourceColors[resource] =
          predefinedColors[index % predefinedColors.length];
      });
      setResourceColors(newResourceColors);

      const newActionColors = {};
      uniqueActions.forEach((action, index) => {
        newActionColors[action] =
          predefinedColors[index % predefinedColors.length];
      });
      setActionColors(newActionColors);
    } catch (error) {
      message.error("Failed to fetch permissions");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const showModal = (permission = null) => {
    setCurrentPermission(permission);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setCurrentPermission(null);
  };

  const handleSubmit = async (
    values,
    customResourceInput,
    customActionInput
  ) => {
    try {
      setLoading(true);
      if (customResourceInput && customResourceInput.trim() !== "") {
        values.resource = customResourceInput.trim();
      }
      if (customActionInput && customActionInput.trim() !== "") {
        values.action = customActionInput.trim();
      }

      if (currentPermission) {
        await updatePermission(currentPermission.id, values);
        message.success("Permission updated successfully");
      } else {
        await createPermission(values);
        message.success("Permission created successfully");
      }

      if (!resources.includes(values.resource)) {
        setResources([...resources, values.resource]);
        setResourceColors({
          ...resourceColors,
          [values.resource]:
            predefinedColors[resources.length % predefinedColors.length],
        });
      }
      if (!actions.includes(values.action)) {
        setActions([...actions, values.action]);
        setActionColors({
          ...actionColors,
          [values.action]:
            predefinedColors[actions.length % predefinedColors.length],
        });
      }

      setIsModalVisible(false);
      setCurrentPermission(null);
      fetchPermissions();
    } catch (error) {
      message.error(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await deletePermission(id);
      message.success("Permission deleted successfully");
      fetchPermissions();
    } catch (error) {
      message.error("Failed to delete permission");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <Title level={2} className="m-0">
          <KeyOutlined className="mr-2 text-green-600" /> Permission Management
        </Title>
        <PermissionRender requiredPermission={["permission", "create"]}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showModal()}
            className="bg-green-600 hover:bg-green-700"
          >
            Add Permission
          </Button>
        </PermissionRender>
      </div>

      <Card className="shadow-md">
        <PermissionTable
          permissions={permissions}
          loading={loading}
          resourceColors={resourceColors}
          actionColors={actionColors}
          resources={resources}
          actions={actions}
          showModal={showModal}
          handleDelete={handleDelete}
        />
      </Card>

      <PermissionModal
        isModalVisible={isModalVisible}
        handleCancel={handleCancel}
        handleSubmit={handleSubmit}
        currentPermission={currentPermission}
        resources={resources}
        actions={actions}
        resourceColors={resourceColors}
        actionColors={actionColors}
        loading={loading}
      />
    </div>
  );
};

export default PermissionsPage;
